import React, { createContext, useContext, useReducer, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { walletPersistence } from '../utils/walletPersistence';

const WalletContext = createContext();

const initialState = {
  wallets: [],
  activeWallet: walletPersistence.load(), // Load from localStorage on init
  isLoading: false,
  error: null,
  isCreating: false,
  isConnecting: false
};

const walletReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
        error: null
      };
    
    case 'SET_CREATING':
      return {
        ...state,
        isCreating: action.payload,
        error: null
      };
    
    case 'SET_CONNECTING':
      return {
        ...state,
        isConnecting: action.payload,
        error: null
      };
    
    case 'FETCH_WALLETS_SUCCESS':
      return {
        ...state,
        wallets: action.payload,
        isLoading: false,
        error: null
      };
    
    case 'CREATE_WALLET_SUCCESS':
      // Save newly created wallet as active
      walletPersistence.save(action.payload);
      return {
        ...state,
        wallets: [action.payload, ...state.wallets],
        activeWallet: action.payload,
        isCreating: false,
        error: null
      };
    
    case 'CONNECT_WALLET_SUCCESS':
      // Save newly connected wallet as active
      walletPersistence.save(action.payload);
      return {
        ...state,
        wallets: [action.payload, ...state.wallets],
        activeWallet: action.payload,
        isConnecting: false,
        error: null
      };
    
    case 'DELETE_WALLET_SUCCESS':
      const updatedWallets = state.wallets.filter(w => w.id !== action.payload);
      const newActiveWallet = state.activeWallet?.id === action.payload ? null : state.activeWallet;
      
      // Update localStorage if active wallet was deleted
      if (state.activeWallet?.id === action.payload) {
        walletPersistence.save(null);
      }
      
      return {
        ...state,
        wallets: updatedWallets,
        activeWallet: newActiveWallet,
        error: null
      };
    
    case 'SET_ACTIVE_WALLET':
      // Save to localStorage whenever active wallet changes
      console.log('WalletContext - SET_ACTIVE_WALLET action:', action.payload?.name || 'null', `(ID: ${action.payload?.id || 'null'})`);
      walletPersistence.save(action.payload);
      return {
        ...state,
        activeWallet: action.payload
      };
    
    case 'SET_ERROR':
      return {
        ...state,
        isLoading: false,
        isCreating: false,
        isConnecting: false,
        error: action.payload
      };
    
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };
    
    case 'RESET':
      // Clear localStorage when resetting
      walletPersistence.clear();
      return {
        ...initialState,
        activeWallet: null // Ensure activeWallet is null on reset
      };
    
    default:
      return state;
  }
};

export const WalletProvider = ({ children }) => {
  const [state, dispatch] = useReducer(walletReducer, initialState);
  const { tokens, isAuthenticated } = useAuth();

  // Use Node.js backend for real Solana wallet generation
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safepumback.civicstech.name.ng/api';
  // const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost/solana-trading-bot/api';

  // Fetch wallets when user is authenticated
  useEffect(() => {
    console.log('WalletContext - useEffect triggered. isAuthenticated:', isAuthenticated, 'tokens:', !!tokens);
    if (isAuthenticated && tokens) {
      console.log('WalletContext - Fetching wallets...');
      fetchWallets();
    } else {
      console.log('WalletContext - User not authenticated, but NOT resetting wallet state to preserve selection');
      // Don't reset wallet state immediately - only clear when user explicitly logs out
      // This allows the wallet selection to persist during page reloads while auth is loading
    }
  }, [isAuthenticated, tokens]);

  const makeAuthenticatedRequest = async (url, options = {}) => {
    if (!tokens?.access_token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${tokens.access_token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}`);
    }

    return response.json();
  };

  const fetchWallets = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      const data = await makeAuthenticatedRequest('/wallets');
      console.log('WalletContext - Fetched wallets:', data.data.wallets);
      dispatch({ type: 'FETCH_WALLETS_SUCCESS', payload: data.data.wallets });
      
      // Handle active wallet restoration from localStorage
      const storedActiveWallet = walletPersistence.load();
      console.log('WalletContext - Available wallets:', data.data.wallets.map(w => ({ id: w.id, name: w.name })));
      
      if (data.data.wallets.length > 0) {
        if (storedActiveWallet) {
          // Check if the stored active wallet still exists in the user's wallets
          const walletStillExists = data.data.wallets.find(w => w.id === storedActiveWallet.id);
          
          if (walletStillExists) {
            console.log('🔄 Restoring active wallet from localStorage:', walletStillExists.name);
            // Update the active wallet with fresh data from the server
            dispatch({ type: 'SET_ACTIVE_WALLET', payload: walletStillExists });
          } else {
            console.log('⚠️ Stored wallet no longer exists, clearing and setting first wallet');
            // Stored wallet no longer exists, clear it and set first wallet
            dispatch({ type: 'SET_ACTIVE_WALLET', payload: data.data.wallets[0] });
          }
        } else {
          // No stored wallet, set first wallet as active only if no active wallet is currently set
          if (!state.activeWallet) {
            console.log('🆕 No stored wallet and no current active wallet, auto-setting first wallet as active:', data.data.wallets[0].name);
            dispatch({ type: 'SET_ACTIVE_WALLET', payload: data.data.wallets[0] });
          } else {
            console.log('✅ No stored wallet but active wallet exists, keeping current selection');
          }
        }
      } else {
        console.log('🚫 No wallets available, clearing active wallet');
        dispatch({ type: 'SET_ACTIVE_WALLET', payload: null });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  }, [state.activeWallet, makeAuthenticatedRequest]);

  const createPlatformWallet = useCallback(async () => {
    dispatch({ type: 'SET_CREATING', payload: true });
    
    try {
      const data = await makeAuthenticatedRequest('/wallets', {
        method: 'POST',
        body: JSON.stringify({})
      });
      
      dispatch({ type: 'CREATE_WALLET_SUCCESS', payload: data.data.wallet });
      return { success: true, wallet: data.data.wallet };
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, error: error.message };
    }
  }, [makeAuthenticatedRequest]);

  const connectExternalWallet = useCallback(async (walletAddress) => {
    if (!walletAddress || !walletAddress.trim()) {
      dispatch({ type: 'SET_ERROR', payload: 'Wallet address is required' });
      return { success: false, error: 'Wallet address is required' };
    }

    dispatch({ type: 'SET_CONNECTING', payload: true });
    
    try {
      const data = await makeAuthenticatedRequest('/wallets', {
        method: 'POST',
        body: JSON.stringify({ wallet_address: walletAddress.trim() })
      });
      
      dispatch({ type: 'CONNECT_WALLET_SUCCESS', payload: data.data.wallet });
      return { success: true, wallet: data.data.wallet };
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, error: error.message };
    }
  }, [makeAuthenticatedRequest]);

  const deleteWallet = useCallback(async (walletId) => {
    try {
      await makeAuthenticatedRequest(`/wallets/${walletId}`, {
        method: 'DELETE'
      });
      
      dispatch({ type: 'DELETE_WALLET_SUCCESS', payload: walletId });
      return { success: true };
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, error: error.message };
    }
  }, [makeAuthenticatedRequest]);

  const setActiveWallet = useCallback((wallet) => {
    console.log('WalletContext - Setting active wallet:', wallet);
    dispatch({ type: 'SET_ACTIVE_WALLET', payload: wallet });
  }, []);

  const getWalletBalance = useCallback(async (walletId, detailed = false) => {
    try {
      const endpoint = detailed ? `/wallets/${walletId}/balance?detailed=true` : `/wallets/${walletId}/balance`;
      const data = await makeAuthenticatedRequest(endpoint);
      return { success: true, balance: data.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [makeAuthenticatedRequest]);

  const refreshWalletBalance = useCallback(async (walletId) => {
    try {
      const data = await makeAuthenticatedRequest(`/wallets/${walletId}/refresh-balance`, {
        method: 'POST'
      });
      return { success: true, balance: data.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [makeAuthenticatedRequest]);

  const validateWalletBalance = useCallback(async (walletId, tokenMint, amount, includeFees = true) => {
    try {
      const data = await makeAuthenticatedRequest(`/wallets/${walletId}/validate-balance`, {
        method: 'POST',
        body: JSON.stringify({
          token_mint: tokenMint,
          amount: amount,
          include_fees: includeFees
        })
      });
      return { success: true, validation: data.data.validation };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [makeAuthenticatedRequest]);

  const getWalletTransactions = useCallback(async (walletId, limit = 10) => {
    try {
      const data = await makeAuthenticatedRequest(`/wallets/${walletId}/transactions?limit=${limit}`);
      return { success: true, transactions: data.data.transactions };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [makeAuthenticatedRequest]);

  const sendSOL = useCallback(async (walletId, recipientAddress, amount) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      const data = await makeAuthenticatedRequest(`/wallets/${walletId}/send`, {
        method: 'POST',
        body: JSON.stringify({
          recipient_address: recipientAddress,
          amount: parseFloat(amount)
        })
      });
      
      dispatch({ type: 'SET_LOADING', payload: false });
      
      // Refresh wallets to update balances
      await fetchWallets();
      
      return { success: true, transaction: data.data.transaction };
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      dispatch({ type: 'SET_LOADING', payload: false });
      return { success: false, error: error.message };
    }
  }, [makeAuthenticatedRequest, fetchWallets]);

  const checkForDeposits = useCallback(async (walletId) => {
    try {
      const data = await makeAuthenticatedRequest(`/wallets/${walletId}/check-deposits`, {
        method: 'POST'
      });
      
      // Refresh wallets to update balances if new deposits found
      if (data.data.new_deposits.length > 0) {
        await fetchWallets();
      }
      
      return { success: true, deposits: data.data.new_deposits };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [makeAuthenticatedRequest, fetchWallets]);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const clearWalletData = useCallback(() => {
    console.log('WalletContext - Explicitly clearing wallet data');
    dispatch({ type: 'RESET' });
  }, []);

  const getWalletPersistenceDebugInfo = useCallback(() => {
    return {
      ...walletPersistence.getDebugInfo(),
      currentActiveWallet: state.activeWallet ? {
        id: state.activeWallet.id,
        name: state.activeWallet.name,
        type: state.activeWallet.wallet_type
      } : null,
      totalWallets: state.wallets.length,
      walletsList: state.wallets.map(w => ({ id: w.id, name: w.name, type: w.wallet_type }))
    };
  }, [state.activeWallet, state.wallets]);

  // Memoize the value object to prevent unnecessary re-renders
  const value = useMemo(() => ({
    wallets: state.wallets,
    activeWallet: state.activeWallet,
    isLoading: state.isLoading,
    error: state.error,
    isCreating: state.isCreating,
    isConnecting: state.isConnecting,
    fetchWallets,
    createPlatformWallet,
    connectExternalWallet,
    deleteWallet,
    setActiveWallet,
    getWalletBalance,
    refreshWalletBalance,
    validateWalletBalance,
    getWalletTransactions,
    sendSOL,
    checkForDeposits,
    clearError,
    clearWalletData,
    getWalletPersistenceDebugInfo
  }), [state.wallets, state.activeWallet, state.isLoading, state.error, state.isCreating, state.isConnecting, fetchWallets, createPlatformWallet, connectExternalWallet, deleteWallet, setActiveWallet, getWalletBalance, refreshWalletBalance, validateWalletBalance, getWalletTransactions, sendSOL, checkForDeposits, clearError, clearWalletData, getWalletPersistenceDebugInfo]);

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};